// What this mission needs from the player before anything can be graded (CCT-327).
//
// ⚠️ LE JOUEUR L'APPRENAIT AU PIRE MOMENT. Rien, sur la page d'une mission, ne disait qu'un
// contrôleur devait être activé : l'exigence n'apparaissait qu'au retour d'une tâche passée en
// revue, sous la forme « je n'arrive pas à voir ton déploiement ». À cet instant le joueur croit
// que son TRAVAIL est jugé, donc un message de configuration se lit comme un verdict sur lui.
//
// Cette section répond avant : ce que la mission exige, et si c'est prêt. Chaque contrôleur
// ouvre son propre dialogue — celui d'Azure porte le lien de déploiement, visible et copiable,
// parce qu'un `openURL` peut être avalé par un bloqueur de fenêtres sans le moindre signe.
import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { apiFetch } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ServerMessage from './ServerMessage';
import { type MessageKey } from '../i18n';
import { useT } from '../i18n/useT';
import { getThemeColors } from '../utils/themeStyles';
import AzureConnection from './AzureConnection';
import GithubApproval from './GithubApproval';

interface Ctrl { kind: string; ready: boolean; detail?: string; mode?: string; connectionId?: string;
                 // Why nothing can be activated, when the mission names no App (CCT-330).
                 message?: string;
                 // CCT-400: the runtime names the message; the app renders it in the
                 // player's language. `message` stays as the fallback for a server
                 // that has not shipped the key yet.
                 messageKey?: string;
                 /** CCT-401 : la NATURE du message, décidée par l'API. */
                 messageType?: string;
                 // CCT-398 — accorde | refuse | indetermine, when the SOCIETY brings the
                 // subscription. The wording lives here, in the player's language: the server
                 // sends the state, never a sentence to display.
                 accessState?: string;
                 // L'URL « Use this template » du dépôt de départ de l'auteur (CCT-330).
                 templateRepo?: string }

const ICONES: Record<string, string> = { cloud: 'cloud-outline', github: 'logo-github' };

export default function MissionControllers({ missionId, requiresP1 = false }: { missionId: number; requiresP1?: boolean }) {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const c = getThemeColors(isDarkTheme);
  const { getAccessToken, user } = useAuth();

  const [ctrls, setCtrls] = useState<Ctrl[] | null>(null);
  const [ouvert, setOuvert] = useState<string>('');
  const [p1Required, setP1Required] = useState(requiresP1);

  /**
   * The runtime's message, rendered in the PLAYER'S language (CCT-400).
   *
   * ⚠️ THIS LINE USED TO PRINT THE SERVER'S FRENCH SENTENCE AS IS. Measured: a Spanish, Italian
   * or Arabic speaker read French here, in an app that speaks their language everywhere else.
   * The runtime now names the message and the app resolves it, with the dictionary it already
   * downloads.
   *
   * ⚠️ THE FALLBACK CHAIN IS DELIBERATE. An unknown key falls back to the server's English
   * rather than to nothing: a runtime that has not shipped a key yet must not blank the card.
   */
  const messageServeur = (x?: { message?: string; messageKey?: string }) => {
    if (x?.messageKey) {
      const rendu = t(x.messageKey as MessageKey);
      if (rendu && rendu !== x.messageKey) return rendu;
    }
    return x?.message || t('srv.controllers.githubUndefined');
  };

  // ⚠️ CLIENT CENTRAL, PAS `fetch` : un jeton périmé se renouvelle en silence au lieu de laisser
  // la section vide. Une section absente se lit « rien à activer » — le contraire du message.
  const charger = useCallback(async () => {
    // ⚠️ A SIGNED-OUT VISITOR CALLS NO `/me/*`. The mission sheet is part of the SHOWCASE
    // (CCT-253): it opens without an account. The call went out anyway, took a 401, and the
    // interceptor read it as an expired session — hence a cascading sign-out and a remount that
    // swallowed the next click. Measured in the console: 401 on /me/missions/24/controllers, then
    // "signed out on 401 after a failed refresh".
    //
    // ⚠️ AND THERE IS NOTHING TO SHOW: controllers are the PLAYER's state on this mission. A
    // visitor has none; staying silent is the right answer, not an impoverishment.
    if (!user) { setCtrls([]); return; }
    try {
      const d = await apiFetch<any>(`/me/missions/${missionId}/controllers`, {
        token: getAccessToken(), auth: true, background: true,
      });
      setCtrls(Array.isArray(d.controllers) ? d.controllers : []);
      setP1Required(requiresP1 || d.prerequisites?.entraP1?.required === true);
    } catch { /* réseau : la section se tait plutôt que d'affirmer « rien à activer » */ }
  }, [missionId, user, requiresP1]);

  useEffect(() => { void charger(); }, [charger]);

  // Une mission sans contrôleur n'affiche rien : une section vide apprendrait au joueur à ne
  // plus la regarder, et c'est précisément l'écran qu'on veut qu'il lise.
  if (!ctrls || ctrls.length === 0) return null;

  const fermer = () => { setOuvert(''); void charger(); };

  const titre = (k: string) => t(k === 'cloud' ? 'controllers.cloud' : 'controllers.github');
  const sousTitre = (x: Ctrl) => {
    // ⚠️ THE SUBSCRIPTION GUID IS NOT A SUBTITLE HERE (CCT-398). On the player's OWN subscription
    // it identifies what they connected, and that is worth showing. On the company's, it is an
    // identifier they never chose, cannot act on, and would only read as noise.
    if (x.kind === 'cloud' && x.mode === 'company') return t('controllers.cloudCompany');
    if (x.detail) return x.detail;
    if (x.kind === 'github' && x.mode === 'app') return t('controllers.notActivated');
    if (x.kind === 'github' && x.mode === 'company') return t('controllers.githubCompany');
    if (x.kind === 'github' && x.mode === 'none') return t('controllers.githubNoMode');
    return t('controllers.notActivated');
  };

  return (
    <View style={[s.card, { backgroundColor: c.cardBackground, borderColor: c.border }]}>
      <Text style={[s.title, { color: c.textPrimary }]}>{t('controllers.title')}</Text>
      <Text style={[s.help, { color: c.textTertiary }]}>{t('controllers.subtitle')}</Text>

      <View style={{ gap: 8, marginTop: 12 }}>
        {ctrls.map((x) => (
          <View key={x.kind} style={[s.ligne, { borderColor: c.border }]}>
            <Ionicons name={ICONES[x.kind] || 'cube-outline'} size={18} color={c.textSecondary} />
            <View style={{ flex: 1 }}>
              <Text style={[s.nom, { color: c.textPrimary }]}>{titre(x.kind)}</Text>
              <Text style={[s.detail, { color: c.textTertiary }]} numberOfLines={1}>
                {sousTitre(x)}
              </Text>
            </View>
            <View style={[s.puce, {
              borderColor: x.ready && !(x.kind === 'cloud' && p1Required) ? c.success : c.warning,
              backgroundColor: 'transparent',
            }]}>
              <Ionicons name={x.ready && !(x.kind === 'cloud' && p1Required) ? 'checkmark-circle' : 'alert-circle'} size={13}
                color={x.ready && !(x.kind === 'cloud' && p1Required) ? c.success : c.warning} />
              <Text style={[s.puceText, { color: x.ready && !(x.kind === 'cloud' && p1Required) ? c.success : c.warning }]}>
                {t(x.ready && x.kind === 'cloud' && p1Required ? 'prerequisites.checkRequired' : x.ready ? 'controllers.ready' : 'controllers.toActivate')}
              </Text>
            </View>
            <TouchableOpacity onPress={() => setOuvert(x.kind)}
              style={[s.btn, { borderColor: c.border }]}>
              <Text style={[s.btnText, { color: c.textPrimary }]}>
                {t(x.ready ? 'controllers.manage' : 'controllers.activate')}
              </Text>
            </TouchableOpacity>
          </View>
        ))}
        {p1Required && <Text style={{ color: c.warning, lineHeight: 20 }}>{t('prerequisites.connectionOnly')}</Text>}
      </View>

      <Modal visible={!!ouvert} transparent animationType="fade" onRequestClose={fermer}>
        <Pressable style={s.fond} onPress={fermer}>
          <Pressable style={[s.boite, { backgroundColor: c.background, borderColor: c.border }]}
            onPress={() => {}}>
            <View style={s.tete}>
              <Text style={[s.title, { color: c.textPrimary, flex: 1 }]}>
                {ouvert ? titre(ouvert) : ''}
              </Text>
              <TouchableOpacity onPress={fermer} accessibilityLabel={t('common.close')}>
                <Ionicons name="close" size={20} color={c.textSecondary} />
              </TouchableOpacity>
            </View>
            <ScrollView style={{ maxHeight: 520 }}>
              {/* CCT-398 — NOTHING TO CONNECT ON A COMPANY SUBSCRIPTION. Opening the
                  delegation screen here would ask the player to deploy a template into a
                  subscription that is not theirs, for an access nobody needs from them: what
                  they lack is a ROLE, and only an administrator can grant it. */}
              {ouvert === 'cloud' && (ctrls.find((x) => x.kind === 'cloud')?.mode === 'company'
                ? <Text style={{ color: c.textSecondary, fontSize: 13, padding: 12 }}>
                    {t(ctrls.find((x) => x.kind === 'cloud')?.accessState === 'refuse'
                      ? 'controllers.cloudCompanyBlocked' : 'controllers.cloudCompanyReady')}
                  </Text>
                : <AzureConnection />)}
              {/* CCT-330 — THE INSTALLATION IS THE ONLY GITHUB ACCESS. The token model (CCT-305)
                  is gone: it asked the player to create a personal access token and HAND IT to
                  the platform. Without a referenced App there is nothing to install, and saying
                  so beats a screen offering a gesture that leads nowhere — the fault is the
                  author's, and the player must not be left hunting for it. */}
              {ouvert === 'github' && (ctrls.find((x) => x.kind === 'github')?.mode === 'app'
                ? <GithubApproval missionId={missionId}
                    templateRepo={ctrls.find((x) => x.kind === 'github')?.templateRepo || ''} />
                : <ServerMessage type={ctrls.find((x) => x.kind === 'github')?.messageType}>
                    {messageServeur(ctrls.find((x) => x.kind === 'github'))}
                  </ServerMessage>)}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const s = StyleSheet.create({
  card: { borderRadius: 12, borderWidth: 1, padding: 16, marginVertical: 12 },
  title: { fontSize: 16, fontWeight: '700' },
  help: { fontSize: 12, lineHeight: 17, marginTop: 4 },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 10,
           paddingHorizontal: 12, paddingVertical: 10, flexWrap: 'wrap' },
  nom: { fontSize: 13, fontWeight: '600' },
  detail: { fontSize: 11, marginTop: 2 },
  puce: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderRadius: 20,
          paddingHorizontal: 8, paddingVertical: 3 },
  puceText: { fontSize: 11, fontWeight: '600' },
  btn: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 7 },
  btnText: { fontSize: 12, fontWeight: '600' },
  fond: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center',
          justifyContent: 'center', padding: 20 },
  boite: { width: '100%', maxWidth: 520, borderWidth: 1, borderRadius: 14, padding: 16, gap: 4 },
  tete: { flexDirection: 'row', alignItems: 'center', gap: 8 },
});
